import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { projectService, reportsService } from 'api';
import dayjs from 'dayjs';
import AddMembersDropdown from 'features/projects/components/AddMembersDropdown';
import { ProjectFormModal } from 'features/projects/components/ProjectFormModal';
import { useProjectActions } from 'features/projects/hooks/useProjectActions';
import { MoreHorizontal, Pencil, Trash2, UserPlus } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmDialog } from 'shared/components/ConfirmDialog';
import { queryKeys } from 'shared/constants/queryKeys';
import { ROUTE_PATHS } from 'shared/constants/routes';
import { usePermissions } from 'shared/hooks/usePermissions';

import {
  ClientDetailPageRoot,
  ClientKpiCard,
  ClientKpiDot,
  ClientKpiGrid,
  ClientKpiLabel,
  ClientKpiLabelRow,
  ClientKpiLabelSub,
  ClientKpiValue,
  DETAIL_ACTIONS_DIVIDER_SX,
  DETAIL_ACTIONS_MENU_ITEM_ACTIVE_SX,
  DETAIL_ACTIONS_MENU_ITEM_SX,
  DETAIL_ACTIONS_MENU_LIST_SX,
  DETAIL_ACTIONS_MENU_PAPER_SX,
  DetailActionsDeleteMenuItem,
  DetailHero,
  DetailHeroActionsRow,
  DetailHeroBlobBottomLeft,
  DetailHeroBlobMid,
  DetailHeroBlobTopRight,
  DetailHeroBreadcrumbLink,
  DetailHeroBreadcrumbs,
  DetailHeroBreadcrumbSeparatorIcon,
  DetailHeroContent,
  DetailHeroDotTexture,
  DetailHeroNameColumn,
  DetailHeroPillDot,
  DetailHeroPillsRow,
  DetailHeroSinceText,
  DetailHeroSubtitle,
  DetailHeroTitle,
  DetailHeroTitleRow,
  DetailHeroTitleStatusRow,
  HeroGlassIconButton,
  LoadingContainer,
  NOT_FOUND_BACK_LINK_SX,
  PROJECT_KPI_GRID_SX,
  ProjectHeroActionButton,
  ProjectHeroStatusPill,
} from '../clients.styles';

import ClientProjectMembersSection from './ClientProjectMembersSection';
import { formatTrackedHM } from './ClientProjectsTab';

// Same "all time" range hack ClientProjectsTab/ProjectDetailPage use for their own
// all-time stats, since the reports summary endpoint requires a bounded date range.
const ALL_TIME_FROM = '2000-01-01';

const ClientProjectDetailPage: React.FC = () => {
  const { clientId, projectId } = useParams<{ clientId: string; projectId: string }>();
  const navigate = useNavigate();
  const { canEditProject, canDeleteProject, canAssignProjectMembers } = usePermissions();
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<HTMLElement | null>(null);
  const [addMemberAnchor, setAddMemberAnchor] = useState<HTMLElement | null>(null);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  // Same data source, mutations, and dialogs /projects and the Client Details Projects
  // tab use — see useProjectActions — so Edit/Archive/Delete behave identically here.
  const {
    clients,
    isLoading,
    getProject,
    projectModal,
    openEditModal,
    closeModal,
    handleModalSubmit,
    createMutation,
    updateMutation,
    deleteDialog,
    requestDelete,
    confirmDelete,
    cancelDelete,
    deleteMutation,
  } = useProjectActions();

  const project = projectId ? getProject(projectId) : undefined;
  const client = clientId ? clients.find((c) => c.id === clientId) : undefined;

  // Same /projects/{id}/members endpoint ProjectAccessTab (rendered below) fetches —
  // React Query dedupes the identical key, so this doesn't cost an extra request.
  const { data: members = [] } = useQuery({
    queryKey: queryKeys.projects.members(projectId ?? ''),
    queryFn: () => projectService.getProjectMembers(projectId!).then((r) => r.data.data),
    enabled: Boolean(projectId),
  });

  // Same query (same queryKey, same queryFn) ClientProjectMembersSection uses for its
  // own "Total" column — must stay byte-identical, not just same key: React Query
  // dedupes purely by key, so two different transforms on the same key would race and
  // whichever query "wins" silently hands its own transformed shape to the other
  // component. Both derive what they need from this same raw groups array instead.
  const { data: reportGroups } = useQuery({
    queryKey: queryKeys.reports.summary({
      projectId,
      groupBy: 'user',
      from: ALL_TIME_FROM,
      to: today,
    }),
    queryFn: () =>
      reportsService
        .getSummary({ projectId: projectId!, groupBy: 'user', from: ALL_TIME_FROM, to: today })
        .then((r) => r.data.data.groups),
    enabled: Boolean(projectId),
  });

  // "Active Contributors" matches the exact definition used by the Clients list page's
  // activeContributorCount and the Client Details Projects tab's "Contributors" column:
  // an active-membership member who has logged at least one time entry on this project.
  const contributorUserIds = useMemo(
    () =>
      new Set(
        (Array.isArray(reportGroups) ? reportGroups : [])
          .map((g) => g.key)
          .filter((k): k is string => Boolean(k)),
      ),
    [reportGroups],
  );

  const assignedUserIds = useMemo(() => new Set(members.map((m) => m.userId)), [members]);

  const activeContributorCount = members.filter(
    (m) => m.workspaceMembershipStatus !== 'Inactive' && m.projectMemberStatus !== 'Inactive' && contributorUserIds.has(m.userId),
  ).length;

  const totalHoursSecs = (project?.trackedHours ?? 0) * 3600;
  const billableHoursSecs = project?.isBillable ? totalHoursSecs : 0;

  const closeMoreMenu = () => setMoreMenuAnchor(null);

  const handleEditClick = () => {
    closeMoreMenu();
    if (projectId) openEditModal(projectId);
  };


  const handleDeleteClick = () => {
    closeMoreMenu();
    if (projectId) requestDelete(projectId);
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  if (!project) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} mt={8}>
        <Typography variant="body2" color="text.secondary">
          {`Project "${projectId}" was not found.`}
        </Typography>
        <Typography
          component="button"
          onClick={() => navigate(clientId ? ROUTE_PATHS.CLIENT_DETAIL(clientId) : ROUTE_PATHS.CLIENTS)}
          sx={NOT_FOUND_BACK_LINK_SX}
        >
          {client ? `Back to ${client.name}` : 'Back to Clients'}
        </Typography>
      </Box>
    );
  }

  return (
    <ClientDetailPageRoot>
      <DetailHero>
        <DetailHeroBlobTopRight aria-hidden />
        <DetailHeroBlobMid aria-hidden />
        <DetailHeroBlobBottomLeft aria-hidden />
        <DetailHeroDotTexture aria-hidden />
        <DetailHeroContent>
          <DetailHeroBreadcrumbs separator={<DetailHeroBreadcrumbSeparatorIcon />}>
            <DetailHeroBreadcrumbLink underline="hover" onClick={() => navigate(ROUTE_PATHS.CLIENTS)}>
              Clients
            </DetailHeroBreadcrumbLink>
            {client && (
              <DetailHeroBreadcrumbLink
                underline="hover"
                onClick={() => navigate(ROUTE_PATHS.CLIENT_DETAIL(client.id))}
              >
                {client.name}
              </DetailHeroBreadcrumbLink>
            )}
            <Typography color="#fff" fontSize={14} fontWeight={500}>
              {project.name}
            </Typography>
          </DetailHeroBreadcrumbs>

          <DetailHeroTitleRow>
            <DetailHeroNameColumn>
              <DetailHeroTitleStatusRow>
                <DetailHeroTitle>{project.name}</DetailHeroTitle>
                <ProjectHeroStatusPill>
                  <DetailHeroPillDot dotStatus={project.status} />
                  {project.status === 'Active' ? 'Active' : project.status === 'OnHold' ? 'On Hold' : 'Inactive'}
                </ProjectHeroStatusPill>
              </DetailHeroTitleStatusRow>
              <DetailHeroSubtitle>{client?.name ?? project.clientName ?? 'Internal project'}</DetailHeroSubtitle>
              <DetailHeroPillsRow>
                <DetailHeroSinceText>
                  Started {dayjs(project.createdAt).format('D MMM YYYY')}
                </DetailHeroSinceText>
              </DetailHeroPillsRow>
            </DetailHeroNameColumn>

            {(canAssignProjectMembers || canEditProject || canDeleteProject) && (
              <DetailHeroActionsRow>
                {canAssignProjectMembers && (
                  <ProjectHeroActionButton
                    type="button"
                    onClick={(e) => {
                      setAddMemberAnchor(e.currentTarget);
                      setAddMemberOpen(true);
                    }}
                  >
                    <UserPlus size={18} strokeWidth={2} />
                    Add Member
                  </ProjectHeroActionButton>
                )}
                {(canEditProject || canDeleteProject) && (
                  <HeroGlassIconButton
                    type="button"
                    aria-label="More actions"
                    onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                  >
                    <MoreHorizontal size={18} />
                  </HeroGlassIconButton>
                )}
              </DetailHeroActionsRow>
            )}
          </DetailHeroTitleRow>

          <ClientKpiGrid sx={PROJECT_KPI_GRID_SX}>
            <ClientKpiCard>
              <ClientKpiLabelRow>
                <ClientKpiDot dotcolor="#A5B4FC" />
                <ClientKpiLabel>
                  Total Hours <ClientKpiLabelSub>· Overall (lifetime)</ClientKpiLabelSub>
                </ClientKpiLabel>
              </ClientKpiLabelRow>
              <ClientKpiValue>{formatTrackedHM(totalHoursSecs)}</ClientKpiValue>
            </ClientKpiCard>
            <ClientKpiCard>
              <ClientKpiLabelRow>
                <ClientKpiDot dotcolor="#34D399" />
                <ClientKpiLabel>Billable Hours</ClientKpiLabel>
              </ClientKpiLabelRow>
              <ClientKpiValue>{formatTrackedHM(billableHoursSecs)}</ClientKpiValue>
            </ClientKpiCard>
            <ClientKpiCard>
              <ClientKpiLabelRow>
                <ClientKpiDot dotcolor="#7DD3FC" />
                <ClientKpiLabel>Mapped Members</ClientKpiLabel>
              </ClientKpiLabelRow>
              <ClientKpiValue>{members.length}</ClientKpiValue>
            </ClientKpiCard>
            <ClientKpiCard>
              <ClientKpiLabelRow>
                <ClientKpiDot dotcolor="#FDBA74" />
                <ClientKpiLabel>Active Contributors</ClientKpiLabel>
              </ClientKpiLabelRow>
              <ClientKpiValue>{activeContributorCount}</ClientKpiValue>
            </ClientKpiCard>
          </ClientKpiGrid>
        </DetailHeroContent>
      </DetailHero>

      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={closeMoreMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: DETAIL_ACTIONS_MENU_PAPER_SX } }}
        sx={DETAIL_ACTIONS_MENU_LIST_SX}
      >
        {canEditProject && (
          <MenuItem sx={DETAIL_ACTIONS_MENU_ITEM_ACTIVE_SX} onClick={handleEditClick}>
            <Pencil size={16} />
            Edit project
          </MenuItem>
        )}
        {canEditProject && canDeleteProject && <Divider sx={DETAIL_ACTIONS_DIVIDER_SX} />}
        {canDeleteProject && (
          <DetailActionsDeleteMenuItem sx={DETAIL_ACTIONS_MENU_ITEM_SX} onClick={handleDeleteClick}>
            <Trash2 size={16} />
            Delete project
          </DetailActionsDeleteMenuItem>
        )}
      </Menu>

      <ConfirmDialog
        open={deleteDialog.open}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={() =>
          confirmDelete(() =>
            navigate(clientId ? ROUTE_PATHS.CLIENT_DETAIL(clientId) : ROUTE_PATHS.CLIENTS),
          )
        }
        onCancel={cancelDelete}
        loading={deleteMutation.isPending}
      />

      <ProjectFormModal
        open={projectModal.open}
        project={project}
        clients={clients}
        onClose={closeModal}
        onSubmit={handleModalSubmit}
        isPending={createMutation.isPending || updateMutation.isPending}
      />

      {/* Same AddMembersDropdown ProjectAccessTab's own "Add member" button uses,
          controlled here so the hero's "Add Member" button can trigger it too. */}
      <AddMembersDropdown
        projectId={project.id}
        assignedUserIds={assignedUserIds}
        open={addMemberOpen}
        onOpenChange={setAddMemberOpen}
        anchorEl={addMemberAnchor}
        hideTrigger
      />

      <ClientProjectMembersSection projectId={project.id} projectName={project.name} />
    </ClientDetailPageRoot>
  );
};

export default ClientProjectDetailPage;
